import { Button } from "components/Button/Button";
import { stringToFormFieldComponent } from "components/Form";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { DateTimeFormComponent } from "components/Form/components/DateTime/DateTimeFormComponent";
import { Form } from "components/Form/components/Form/Form";
import { FormComponentPropLanguages } from "components/Form/components/FormComponentProps";
import { LocationFormField } from "components/Form/components/LocationFormField/LocationFormField";
import { RadioGroupFormComponent } from "components/Form/components/RadioGroup/RadioGroupFormComponent";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { seedSchemaFormValues } from "components/Form/components/SchemaForm/seedSchemaFormValues";
import { SwitchFormComponent } from "components/Form/components/Switch/SwitchFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import { Modal } from "components/Modal/Modal";
import NotificationService from "components/Notification/Notification.service";
import { rolesAutocompleteSelector } from "components/RoleSelect/RoleSelect";
import { RoleSelectAutocompleteFormComponent } from "components/RoleSelect/RoleSelectAutocompleteFormComponent";
import { ResourceConfig } from "config/config";
import { fetchUserList } from "containers/Auth/screens/IdentityManagement/UserList.service";
import {
    actions as resourceActions,
    actions as resourcesActions,
} from "containers/Resources/Resources.reducer";
import { Action } from "containers/Resources/components/CommonActions/Action";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    getReadOnlyOrDeleted,
    isDeleted,
    migrateDeprecationsActionSignature,
    MigrateDeprecationsListAction,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import { getSelectedLocationKey } from "containers/Resources/components/CommonActions/MoveToLocation/MoveResourcesToLocation";
import { useMoveToLocation } from "containers/Resources/components/CommonActions/MoveToLocation/useMoveToLocation";
import {
    createWebhook,
    isCreateScheduleDisabled,
} from "containers/Resources/components/CommonActions/utils";
import {
    ExecutionPriorityField,
    setDefaultPriorityMutator,
    useGetPriority,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/ExecutionPriorityField";
import {
    MaxRuntimeMinutesField,
    useMaxRuntimeMinutes,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/MaxRuntimeMinutesField";
import {
    createScheduleAction,
    CreateScheduleDropdownContent,
    getAdditionalSchedulerList,
    getSchedulingBundleSchedulerId,
} from "containers/Resources/screens/ResourcesSingle/components/UsedByList/CreateScheduleButton";
import {
    commentsTab,
    inputSchemaTab,
    inputSchemaTabKey,
    logsTab,
    metadataTab,
    outputSchemaTab,
    usedBySchedulesTab,
    usedByWebhooksTab,
    valueAndSchemaInput,
    valueAndSchemaOutput,
    wrappersTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { executionPriorityColumn } from "containers/Search/components/SearchTabs/columns";
import dayjs from "dayjs";
import { User } from "graphql/generated/graphql";
import { YAMLException } from "js-yaml";
import { isEqual } from "lodash";
import restClient from "api/restClient";
import { convertTime } from "config/ts-utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { useSelector } from "react-redux";
import { useMount } from "react-use";
import { getRelativeUrl } from "utils/service";
import { parseYaml } from "utils/valueTransformers";
import resource from "./resource";
import { getLatestExecutions } from "./sharedFields";
import {
    scriptField,
    valueAndSchemaInputField,
    valueAndSchemaOutputField,
} from "./sharedFieldsTyped";

type FlowStats = {
    p75_seconds: number | null;
    sample_count: number;
    success_rate: number | null;
    trend_ratio: number | null;
};

const FlowRuntimeStats = ({ flowId }: { flowId: string }) => {
    const [stats, setStats] = useState<FlowStats | null>(null);

    useEffect(() => {
        restClient
            .get(`/flow/${flowId}/runtime_stats`)
            .then((r) => setStats(r.data))
            .catch(() => {});
    }, [flowId]);

    if (!stats?.p75_seconds) return null;

    const { p75_seconds, sample_count, success_rate, trend_ratio } = stats;

    return (
        <div style={{ fontSize: "0.85em", lineHeight: 1.6 }}>
            <div>
                <span style={{ opacity: 0.8 }}>Typical runtime:</span>{" "}
                <strong>~{convertTime(p75_seconds)}</strong>
                <span style={{ opacity: 0.6, marginLeft: 6 }}>({sample_count} runs)</span>
            </div>
            {success_rate !== null && (
                <div>
                    <span style={{ opacity: 0.8 }}>Success rate:</span>{" "}
                    <strong
                        style={{
                            color:
                                success_rate >= 0.9
                                    ? "#2e7d32"
                                    : success_rate >= 0.7
                                    ? "#e65100"
                                    : "#c62828",
                        }}
                    >
                        {Math.round(success_rate * 100)}%
                    </strong>
                </div>
            )}
            {trend_ratio !== null && trend_ratio > 1.2 && (
                <div style={{ color: "#e65100" }}>▲ Running slower recently</div>
            )}
            {trend_ratio !== null && trend_ratio < 0.8 && (
                <div style={{ color: "#2e7d32" }}>▼ Running faster recently</div>
            )}
        </div>
    );
};

const removeAllBreakpointsAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: resourceActions.START_UPDATE_RESOURCE,
            promise: { resolve, reject },
            resourceName: "flow",
            variables: {
                id: data.resourceData?.id,
                breakpoints: [],
            },
        });
    }).then(() => {
        NotificationService.success("All breakpoints removed from flow");
    });
};

const runExecutionAction: Action = (
    data,
    dispatch,
    history,
    { isProductive, useBreakpoints, useExplicitBreakpoints, additionalVariables = {} }
) =>
    new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_CREATE_RESOURCE,
            promise: { resolve, reject },
            resourceName: "execution",
            variables: {
                type: "FLOW",
                flow_id: data.resourceData?.id,
                input_value: {},
                is_productive: isProductive,
                use_breakpoints: useBreakpoints,
                use_only_explicit_breakpoints: useExplicitBreakpoints,
                ...additionalVariables,
            },
        });
    }).then((newResourceData: any) => {
        if (newResourceData?.id) {
            history.push(getRelativeUrl("execution", newResourceData?.id));
        }
    });

const runAction: Action = (data, dispatch, history, additionalVariables) =>
    runExecutionAction(data, dispatch, history, {
        isProductive: true,
        useBreakpoints: false,
        additionalVariables,
    });

const tryAction: Action = (data, dispatch, history) =>
    runExecutionAction(data, dispatch, history, { isProductive: false, useBreakpoints: false });

const debugAction: Action = (data, dispatch, history) =>
    runExecutionAction(data, dispatch, history, { isProductive: false, useBreakpoints: true });

const debugFixedBreakpointsAction: Action = (data, dispatch, history) =>
    runExecutionAction(data, dispatch, history, {
        isProductive: false,
        useBreakpoints: true,
        useExplicitBreakpoints: true,
    });

/**
 * Width of the "Run with options" dialog. The form is laid out in two columns
 * (run options left, input value + roles right) so the whole form fits without
 * inner scrolling on a typical desktop viewport; Modal.scss caps it at the
 * viewport width and collapses to one column on narrow screens
 * (ui-design-principle-modals-dialogs: explicit width from the size scale).
 */
const RUN_WITH_OPTIONS_MODAL_WIDTH = "1040px";

const RunWithOptions = ({ actionData, dispatch, history, isDisabled, saveForm }: any) => {
    const [isOpen, setIsOpen] = useState(false);

    const { getPriorityValue, defaultPriorityValue } = useGetPriority();
    const {
        getMaxRuntimeMinutesInitialValue,
        getMaxRuntimeMinutesSubmitValue,
        workspaceMaxRuntimeMinutesValue,
    } = useMaxRuntimeMinutes();

    const userListSelector = (state: any) => state?.dataStore?.allUsers?.user_list?.user_list ?? [];
    const userList: User[] = useSelector(userListSelector);
    const currentUserRoleList = useMemo(
        () =>
            userList
                .find((entry) => entry.id === actionData?.userData?.identity_id)
                ?.identity_role_plain_list_identity_id?.reduce(
                    (roles: { name: string; id: string; propagate: boolean }[], currentRole) => {
                        const roleWithPropagate = currentRole?.propagate
                            ? {
                                  name: currentRole?.role_id_role?.name ?? "",
                                  id: currentRole.role_id_role?.id ?? "",
                                  propagate: true,
                              }
                            : null;
                        return roleWithPropagate ? [...roles, roleWithPropagate] : roles;
                    },
                    []
                ) ?? [],
        [actionData?.userData?.identity_id, userList]
    );
    const roles = useSelector(rolesAutocompleteSelector).map((role) => ({
        ...role,
        propagate: false,
    }));

    const defaultLocationId =
        actionData.resourceData?.project_id ?? actionData.resourceData?.bundle_id;
    const { selectedLocation, changeValue, options } = useMoveToLocation("flow");

    useMount(() => {
        fetchUserList(dispatch);
    });

    const initialValues = useMemo(
        () => ({
            is_productive: true,
            use_wrappers: true,
            run_later: "false",
            scheduled_for: null,
            run_here: "true",
            roles: currentUserRoleList,
            location: defaultLocationId,
            inputValueAndSchema: {
                // Seed the editable value model with the schema's effective defaults so
                // the code-view and submit payload match what the form view renders on
                // open (otherwise the model is `null`: running unchanged drops the shown
                // defaults, and the first edit greys out every other field). See
                // seedSchemaFormValues for the full rationale.
                values: seedSchemaFormValues(actionData.resourceData?.input_schema),
                schema: actionData.resourceData?.input_schema,
            },
            priority: getPriorityValue(actionData.resourceData?.priority),
            max_runtime_minutes: getMaxRuntimeMinutesInitialValue(
                actionData.resourceData?.max_runtime_minutes
            ),
        }),
        [
            actionData.resourceData?.input_schema,
            actionData.resourceData?.max_runtime_minutes,
            actionData.resourceData?.priority,
            currentUserRoleList,
            defaultLocationId,
            getMaxRuntimeMinutesInitialValue,
            getPriorityValue,
        ]
    );

    const onSubmit = useCallback(
        (formData) => {
            const unwrapped = formData.use_wrappers != null ? !formData.use_wrappers : true;
            const scheduled_for =
                (formData.run_later === "true" && dayjs(formData.scheduled_for).format()) ||
                initialValues.scheduled_for;
            const remote_workspace_name =
                (formData.run_here === "false" && formData.remote_workspace_name) || null;
            const roles = formData.roles.map(({ name, propagate }) => ({
                name,
                propagate: propagate ?? false,
            }));
            let location = {};
            if (selectedLocation) {
                location = {
                    [getSelectedLocationKey(selectedLocation.type!)]: formData.location,
                };
            }
            const additionalVariables = {
                unwrapped,
                scheduled_for,
                is_productive: formData.is_productive,
                remote_workspace_name,
                roles,
                input_value: formData.inputValueAndSchema.values,
                priority: getPriorityValue(formData.priority),
                max_runtime_minutes: getMaxRuntimeMinutesSubmitValue(formData.max_runtime_minutes),
                ...location,
            };

            runAction(actionData, dispatch, history, additionalVariables);
        },
        [
            actionData,
            dispatch,
            getMaxRuntimeMinutesSubmitValue,
            getPriorityValue,
            history,
            initialValues.scheduled_for,
            selectedLocation,
        ]
    );

    const formRef = useRef<any>(null);

    return (
        <>
            <Button
                onClick={() => saveForm().then(() => setIsOpen(true))}
                disabled={isDisabled}
                variation="clear"
                label="Run with options"
            />
            <Modal
                title="Run flow with options"
                onCancel={() => {
                    setIsOpen(false);
                    formRef.current.restart();
                }}
                isVisible={isOpen}
                className="runWithOptionsModal"
                width={RUN_WITH_OPTIONS_MODAL_WIDTH}
                closable={false}
            >
                <FinalForm
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    mutators={{
                        setDefaultPriority: (args, state, utils) =>
                            setDefaultPriorityMutator({ state, utils, defaultPriorityValue }),
                    }}
                >
                    {({ handleSubmit, values, form, pristine }) => {
                        formRef.current = form;

                        return (
                            <Form onSubmit={handleSubmit}>
                                <div className="runWithOptionsFields">
                                    <div className="runWithOptionsFields__options">
                                        <Form.Row>
                                            <Field
                                                name="is_productive"
                                                id="is_productive"
                                                fieldProps={{
                                                    label: "Productive",
                                                }}
                                                component={SwitchFormComponent}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="use_wrappers"
                                                id="use_wrappers"
                                                fieldProps={{
                                                    label: "Use wrappers",
                                                }}
                                                component={SwitchFormComponent}
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <ExecutionPriorityField
                                                label="Execution priority"
                                                onBlur={() => {
                                                    if (!pristine) {
                                                        form.mutators.setDefaultPriority();
                                                    }
                                                }}
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <MaxRuntimeMinutesField
                                                label="Max runtime minutes"
                                                recordType="flow"
                                                workspaceMaxRuntimeMinutesValue={
                                                    workspaceMaxRuntimeMinutesValue
                                                }
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <Field
                                                name="run_later"
                                                id="run_later"
                                                fieldProps={{
                                                    options: [
                                                        {
                                                            label: "Run now",
                                                            value: "false",
                                                        },
                                                        {
                                                            label: "Run later",
                                                            value: "true",
                                                        },
                                                    ],
                                                }}
                                                fieldAttr={{ row: true }}
                                                component={RadioGroupFormComponent}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="scheduled_for"
                                                id="scheduled_for"
                                                component={DateTimeFormComponent}
                                                fieldAttr={{
                                                    disabled: values?.run_later === "false",
                                                }}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="run_here"
                                                id="run_here"
                                                fieldProps={{
                                                    options: [
                                                        {
                                                            label: "Run here",
                                                            value: "true",
                                                        },
                                                        {
                                                            label: "Run in remote workspace",
                                                            value: "false",
                                                        },
                                                    ],
                                                }}
                                                fieldAttr={{ row: true }}
                                                component={RadioGroupFormComponent}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="remote_workspace_name"
                                                id="remote_workspace_name"
                                                component={AutocompleteFormComponent}
                                                fieldAttr={{
                                                    disabled: values?.run_here === "true",
                                                }}
                                                fieldProps={{
                                                    label: "Sync config",
                                                    options: {
                                                        resource: "sync_config",
                                                        hasNameAsOptionValue: true,
                                                    },
                                                }}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <LocationFormField
                                                locationId={defaultLocationId}
                                                changeValue={changeValue}
                                                selectedLocation={selectedLocation}
                                                options={options}
                                                label="Run in location"
                                                isRequired={false}
                                            />
                                        </Form.Row>
                                    </div>
                                    <div className="runWithOptionsFields__input">
                                        <Form.Row>
                                            <Field
                                                name="inputValueAndSchema"
                                                component={stringToFormFieldComponent.schemaForm}
                                                fieldProps={{
                                                    lang: "yaml" as FormComponentPropLanguages,
                                                    isReadOnly: false,
                                                    columnDirection: true,
                                                    label: "Input value",
                                                }}
                                                isEqual={(valueA, valueB) => {
                                                    return isEqual(valueA, valueB);
                                                }}
                                                validation={(value) => {
                                                    try {
                                                        parseYaml(value.values);
                                                        return undefined;
                                                    } catch (error: any) {
                                                        const typedError: YAMLException = error;
                                                        const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
                                                        return errorMessage;
                                                    }
                                                }}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <RoleSelectAutocompleteFormComponent
                                                name="roles"
                                                defaultValue={currentUserRoleList}
                                                roleOptions={roles}
                                            />
                                        </Form.Row>
                                    </div>
                                </div>
                                <Form.Row className="sticky-bottom-actions">
                                    <CancelButton
                                        onClick={() => {
                                            setIsOpen(false);
                                            form.restart();
                                        }}
                                    />
                                    <Button submit label="Run" />
                                </Form.Row>
                            </Form>
                        );
                    }}
                </FinalForm>
            </Modal>
        </>
    );
};

const resourceName = "flow";
const config: ResourceConfig = {
    label: "Flow",
    icon: IconCollection.codeTags,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: inputSchemaTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [
                    inputSchemaTab.tab,
                    outputSchemaTab.tab,
                    wrappersTab.tab,
                    usedBySchedulesTab.tab,
                    usedByWebhooksTab.tab,
                    logsTab.tab,
                    metadataTab.tab,
                    commentsTab.tab,
                ],
                content: [
                    inputSchemaTab.content,
                    outputSchemaTab.content,
                    wrappersTab.content,
                    usedBySchedulesTab.content,
                    usedByWebhooksTab.content,
                    logsTab.content,
                    metadataTab.content,
                    commentsTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [
            {
                key: "run",
                action: runAction,
                label: () => "Run",
                icon: () => IconCollection.play,
                disableWhen: isDeleted,
                isDropdown: () => true,
                dropdownContent: (props) => <RunWithOptions {...props} />,
            },
            // Added use_only_explicit_breakpoints  boolean flag to execution,
            //  setting that to true only injects breakpoints into the flow script
            //   where the user has set some. The step-into button thus acts exactly
            //    like the run button and should not be displayed by the UI if use_only_explicit_breakpoints  is true .
            {
                key: "try",
                action: tryAction,
                label: () => "Run in dev mode",
                icon: () => IconCollection.playSpeedIcon,
                disableWhen: isDeleted,
                isDropdown: () => true,
                dropdownContent: ({ actionData, dispatch, history, isDisabled, saveForm }) => (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                        }}
                    >
                        <Button
                            key={"debug"}
                            label={"Debug"}
                            icon={IconCollection.debugStepOver}
                            disabled={isDisabled}
                            variation="clear"
                            className="wide"
                            onClick={() =>
                                saveForm().then(() => debugAction(actionData, dispatch, history))
                            }
                        />
                        <Button
                            key={"debug-fixed"}
                            label={"Debug (fixed breakpoints)"}
                            icon={IconCollection.debugStepOver}
                            disabled={isDisabled}
                            variation="clear"
                            className="wide"
                            onClick={() =>
                                saveForm().then(() =>
                                    debugFixedBreakpointsAction(actionData, dispatch, history)
                                )
                            }
                        />
                        <Button
                            key={"removeAllBreakpoints"}
                            label={"Remove breakpoints"}
                            icon={IconCollection.circleOffOutline}
                            disabled={
                                getReadOnlyOrDeleted(actionData) ||
                                actionData.resourceData?.breakpoints?.length === 0
                            }
                            variation="clear"
                            className="wide"
                            onClick={() =>
                                removeAllBreakpointsAction(actionData, dispatch, history)
                            }
                        />
                    </div>
                ),
            },
            {
                key: "createSchedule",
                action: (data, dispatch, history) => {
                    const schedulingBundleSchedulerId = getSchedulingBundleSchedulerId(
                        data?.schedulerList
                    );
                    createScheduleAction({
                        recordId: data?.resourceData?.id,
                        schedulerId: schedulingBundleSchedulerId,
                        history,
                    });
                    return Promise.resolve(null);
                },
                label: () => "Create schedule",
                icon: () => IconCollection.timetable,
                disableWhen: (data) => isCreateScheduleDisabled(data?.resourceData),
                isDropdown: (actionData) =>
                    getAdditionalSchedulerList(actionData?.schedulerList).length > 0,
                dropdownContent: ({ actionData, dispatch, history, isDisabled, saveForm }) => {
                    return (
                        <CreateScheduleDropdownContent
                            isFieldDisabled={isDisabled}
                            action={(schedulerId) => {
                                createScheduleAction({
                                    recordId: actionData?.resourceData?.id,
                                    schedulerId,
                                    history,
                                });
                            }}
                            schedulerList={actionData?.schedulerList}
                        />
                    );
                },
            },
            {
                key: createWebhook.key,
                action: (data, dispatch, history) => {
                    createWebhook.action({ flowId: data?.resourceData?.id, history });
                    return Promise.resolve(null);
                },
                label: () => createWebhook.label,
                icon: () => createWebhook.icon,
                disableWhen: (data) => createWebhook.isDisabled(data?.resourceData),
            },
            migrateDeprecationsActionSignature,
        ],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
            { key: "migrateDeprecations", action: MigrateDeprecationsListAction },
        ],
    },
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            script
            input_schema
            output_schema
            breakpoints
            savepoint_retention
            webhook_list_flow_id (limit: 0, order: "+id", offset: 0) {
                webhook_count
            }
            schedule_list_flow_id (limit: 0, order: "+id", offset: 0) {
                schedule_count
            }
            priority
            max_runtime_minutes
            has_deprecation
            has_syntax_error
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            has_deprecation
            has_syntax_error
            priority
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $script: String,
            $input_schema: SchemalessData,
            $output_schema: SchemalessData,
            $breakpoints: [Int],
            $savepoint_retention: SavepointRetention,
            $priority: Int
            $max_runtime_minutes: Int
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            script: $script,
            input_schema: $input_schema,
            output_schema: $output_schema,
            breakpoints: $breakpoints,
            savepoint_retention: $savepoint_retention,
            priority: $priority
            max_runtime_minutes: $max_runtime_minutes
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $script: String
            $input_schema: SchemalessData,
            $output_schema: SchemalessData,
            $savepoint_retention: SavepointRetention,
            $breakpoints: [Int],
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            script: $script
            input_schema: $input_schema,
            output_schema: $output_schema,
            savepoint_retention: $savepoint_retention,
            breakpoints: $breakpoints,
        `,
    },
    additionalColumns: (setNewValue) => [executionPriorityColumn(setNewValue)],
    detailsSidebar: [
        {
            label: "Runtime estimate",
            valueKey: "",
            dto: (flow: any) => {
                if (!flow?.id) return null;
                return <FlowRuntimeStats flowId={flow.id} />;
            },
        },
    ],
    statsSidebar: [getLatestExecutions("flow")],
    form: [scriptField, valueAndSchemaInputField(), valueAndSchemaOutputField],
    formDTO: {
        initialValueDTO: (data: any) => {
            const valueAndSchemaInputFieldValues: SchematizedValues = {
                values: data?.input_value,
                schema: data?.input_schema,
            };
            const valueAndSchemaOutputFieldValues: SchematizedValues = {
                values: data?.output_value,
                schema: data?.output_schema,
            };
            return {
                ...data,
                [valueAndSchemaInput]: valueAndSchemaInputFieldValues,
                [valueAndSchemaOutput]: valueAndSchemaOutputFieldValues,
            };
        },
    },
};
export default config;
